import { mat4, vec3 } from 'gl-matrix';

import { DefaultCamera } from './DefaultCamera';
import { ViewPort } from '../context/ViewPort';
import { toRadian } from '../math';

export class FreeViewCamera extends DefaultCamera {
  private _targetPosition: vec3;
  private _rotation: number;
  private _cameraPosition: vec3;

  private _distance: number;
  private _yOffset: number;
  private _tilt: number;

  constructor(viewPort: ViewPort) {
    super(viewPort);
    this._rotation = 0;
    this._tilt = 0;
    this._targetPosition = vec3.create();
    this._cameraPosition = vec3.create();

    this._distance = 5;
    this._yOffset = 2;
  }

  public get distance(): number {
    return this._distance;
  }

  public set distance(value: number) {
    this._distance = value;
    this.invalidatedModelView = true;
  }

  public get yOffset(): number {
    return this._yOffset;
  }

  public set yOffset(value: number) {
    this._yOffset = value;
    this.invalidatedModelView = true;
  }

  public get tilt(): number {
    return this._tilt;
  }

  public set tilt(value: number) {
    this._tilt = Math.max(-80, Math.min(80, value));
    this.invalidatedModelView = true;
  }

  public get targetPosition(): vec3 {
    return this._targetPosition;
  }

  public get cameraPosition(): vec3 {
    let rotation = this.rotation % 360;
    let tiltRad = toRadian(this._tilt);
    let horizontalDist = this.distance * Math.cos(tiltRad);
    let verticalOffset = this.yOffset + this.distance * Math.sin(tiltRad);

    this._cameraPosition[0] = this.x + horizontalDist * Math.sin(toRadian(rotation));
    this._cameraPosition[1] = this.y + verticalOffset;
    this._cameraPosition[2] = this.z + horizontalDist * Math.cos(toRadian(rotation));

    return this._cameraPosition;
  }

  public get rotation(): number {
    return this._rotation;
  }

  public set rotation(value: number) {
    this._rotation = value;
    this.invalidatedModelView = true;
  }

  public get x(): number {
    return this._targetPosition[0];
  }

  public set x(value: number) {
    this._targetPosition[0] = value;
    this.invalidatedModelView = true;
  }

  public get y(): number {
    return this._targetPosition[1];
  }

  public set y(value: number) {
    this._targetPosition[1] = value;
    this.invalidatedModelView = true;
  }

  public get z(): number {
    return this._targetPosition[2];
  }

  public set z(value: number) {
    this._targetPosition[2] = value;
    this.invalidatedModelView = true;
  }

  public fitToBounds(center: vec3 | number[], size: number) {
    this.x = center[0];
    this.y = center[1];
    this.z = center[2];
    this.distance = size * 1.5;
    this.yOffset = 0;
    this.tilt = 15;
  }

  public rotateInPlace(dYaw: number, dPitch: number): void {
    const camPos = this.cameraPosition;
    const cx = camPos[0];
    const cy = camPos[1];
    const cz = camPos[2];

    this.rotation -= dYaw;
    this.tilt += dPitch;

    const tiltRad = toRadian(this._tilt);
    const rotRad = toRadian(this._rotation);
    const hDist = this._distance * Math.cos(tiltRad);
    const vOffset = this._yOffset + this._distance * Math.sin(tiltRad);

    this._targetPosition[0] = cx - hDist * Math.sin(rotRad);
    this._targetPosition[1] = cy - vOffset;
    this._targetPosition[2] = cz - hDist * Math.cos(rotRad);
    this.invalidatedModelView = true;
  }

  protected applyModelView(matrix: mat4): void {
    /*
        var pitch = this.lookDirection;
        var yaw = -this.rotation;

        mat4.rotate(matrix, matrix, glMatrix.toRadian(-pitch), [1, 0, 0]);
        mat4.rotate(matrix, matrix, glMatrix.toRadian(-yaw), [0, 1, 0]);

        mat4.rotate(matrix, matrix, glMatrix.toRadian(90.0), [1, 0, 0]);

        mat4.translate(matrix, matrix, vec3.fromValues(-this.x, -this.y, -this.z));*/

    mat4.lookAt(
      matrix,
      this.cameraPosition,
      this._targetPosition, // location
      vec3.fromValues(0, 1, 0),
    );
  }

  public prepareFrame(): void {
    this.prepareTemporalFrame();
  }

  public override toString(): string {
    return `${Math.floor(this.x)},${Math.floor(this.y)},${Math.floor(this.z)} FreeViewCamera`;
  }
}
